import { Headset, MapPin, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";

const CUSTOMER_CARE = {
  heading: "Speak to Our Customer Care",
  phone: "0448 915 019",
  tel: "tel:+61448915019",
};

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact GSMG INVESTMENTS | Air Conditioning Supply Enquiries"
        description="Have a residential, commercial or builder supply enquiry? Contact GSMG INVESTMENTS by phone or send your project requirements through our enquiry form."
      />

      {/* Page header */}
      <section className="grain relative overflow-hidden bg-ink pb-16 pt-32 text-white sm:pt-36 lg:pb-20 lg:pt-44">
        <svg className="airflow absolute inset-x-0 top-0 h-full w-full opacity-25" viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <path d="M-60 200 C 380 90, 960 300, 1520 150" stroke="#38BDF8" strokeWidth="1.5" fill="none" />
          <path d="M-60 430 C 420 310, 980 500, 1520 360" stroke="#00A8E8" strokeWidth="1.5" fill="none" />
        </svg>
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-sky-300">Contact</p>
          <h1 data-testid="contact-heading" className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Contact GSMG INVESTMENTS
          </h1>
          <Reveal delay={0.2} y={20}>
            <p className="mt-7 max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Have a residential, commercial or builder supply enquiry? Send us your details and project requirements, and the team at GSMG INVESTMENTS can follow up with you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Customer care contact */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Get in touch</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">{CUSTOMER_CARE.heading}</h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">
              Call our customer care line to discuss cooling equipment and project supply requirements, or send your enquiry through the form below.
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <div data-testid="contact-phone-card" className="flex flex-col items-start rounded-2xl border border-slate-200 bg-mist p-8 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-navy/10 sm:p-10">
              <span className="grid h-12 w-12 place-items-center rounded-lg bg-navy text-white" aria-hidden="true">
                <Headset size={20} />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold text-ink">Customer Care Line</h3>
              <p className="mt-1 text-sm text-slate-500">Residential, commercial and builder supply enquiries</p>
              <a
                href="tel:+61448915019"
                data-testid="contact-phone-link"
                className="mt-5 inline-flex items-center gap-3 font-display text-2xl font-bold text-navy transition-colors hover:text-brand sm:text-3xl"
              >
                <Phone size={24} className="text-brand" aria-hidden="true" />
                0448 915 019
              </a>
              <div className="mt-6 flex items-start gap-3 border-t border-slate-200 pt-6">
                <MapPin size={20} className="mt-0.5 shrink-0 text-brand" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-ink">Address</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">2/55 Amherst Drive, Truganina</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Enquiry form */}
      <section className="bg-mist py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Enquiry form</p>
              <p className="mt-5 text-[15px] leading-relaxed text-slate-600">
                Send us your details and project requirements, and the team at GSMG INVESTMENTS can follow up with you.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md text-sm leading-relaxed text-slate-500">
                Prefer to call? Use our customer care line on 0448 915 019, or use the enquiry form and your details will go directly to our team.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-9">
                <EnquiryForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
